import { styled } from "goober";
import { useState } from "react";
import { Binary } from "./Binary";
import { Icon } from "../Icon";
import { Text } from "../Text";
import { extensionFromMime } from "./download";
import { DownloadButton } from "./DownloadButton";
import type { FileRenderControl } from "./types";
import { useFileActions } from "./useFileActions";
import { useObjectUrl } from "./useObjectUrl";

const Container = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const VideoEl = styled("video")`
  width: 100%;
  max-height: 70vh;
  border-radius: 12px;
  border: 0.5px solid var(--border-alpha);
  background: var(--surface-primary);
`;

type Props = {
  url?: string;
  contentType: string;
  fileName?: string;
  onControls?: (controls: FileRenderControl[]) => void;
};

export function Video({ url, contentType, fileName, onControls }: Props) {
  const { blobUrl, failed } = useObjectUrl(url);
  const [undecodableUrl, setUndecodableUrl] = useState<string>();

  const ext = extensionFromMime(contentType);

  const downloadName = fileName ?? `video.${ext}`;
  const { download, downloading } = useFileActions({
    url,
    fileName: downloadName,
    onControls,
  });

  if (failed)
    return (
      <Binary
        url={url}
        contentType={contentType}
        fileName={fileName}
        hideActions={!!onControls}
        note="Couldn’t load this file"
      />
    );

  if (!blobUrl) return null;

  if (undecodableUrl === blobUrl)
    return (
      <Binary
        url={url}
        contentType={contentType}
        fileName={fileName}
        hideActions={!!onControls}
        note="Can’t be played here"
      />
    );

  return (
    <Container>
      <VideoEl
        controls
        src={blobUrl}
        onError={() => setUndecodableUrl(blobUrl)}
      />
      {!onControls && (
        <div style={{ marginLeft: "auto" }}>
          <DownloadButton onPress={download} disabled={!url || downloading}>
            <Icon name="arrow-down" size={12} />
            <Text variant="text-small-md">
              {downloading ? "Downloading…" : "Download"}
            </Text>
          </DownloadButton>
        </div>
      )}
    </Container>
  );
}
